/**
 * Extract plain text from builder SOP / docs for Ops Brain chat context.
 * Supports text-like files, PDF (pdfjs via react-pdf), and Excel (exceljs).
 */

const MAX_CHARS = 12000;
const NULL_CHAR = String.fromCharCode(0);

function truncate(text, max = MAX_CHARS) {
  const cleaned = String(text || '')
    .split(NULL_CHAR)
    .join('')
    .replace(/\r\n/g, '\n')
    .trim();
  if (cleaned.length <= max) return cleaned;
  return `${cleaned.slice(0, max)}\n\n[…truncated for chat context…]`;
}

async function extractPdfText(file) {
  const pdfjs = await import('pdfjs-dist');
  if (pdfjs.GlobalWorkerOptions) {
    const version = pdfjs.version || '4.8.69';
    pdfjs.GlobalWorkerOptions.workerSrc = `https://cdnjs.cloudflare.com/ajax/libs/pdf.js/${version}/pdf.worker.min.mjs`;
  }
  const data = new Uint8Array(await file.arrayBuffer());
  const doc = await pdfjs.getDocument({ data }).promise;
  const pages = [];
  const limit = Math.min(doc.numPages, 20);
  for (let i = 1; i <= limit; i += 1) {
    const page = await doc.getPage(i);
    const content = await page.getTextContent();
    pages.push(content.items.map((item) => item.str || '').join(' '));
  }
  return pages.join('\n');
}

async function extractExcelText(file) {
  const ExcelJS = (await import('exceljs')).default;
  const workbook = new ExcelJS.Workbook();
  await workbook.xlsx.load(await file.arrayBuffer());
  const lines = [];
  workbook.eachSheet((sheet, sheetId) => {
    lines.push(`# Sheet: ${sheet.name || sheetId}`);
    sheet.eachRow({ includeEmpty: false }, (row, rowNumber) => {
      if (rowNumber > 200) return;
      const values = row.values
        .slice(1)
        .map((v) => {
          if (v == null) return '';
          if (typeof v === 'object' && v.text) return String(v.text);
          if (typeof v === 'object' && v.result != null) return String(v.result);
          return String(v);
        })
        .filter((s) => s.trim().length);
      if (values.length) lines.push(values.join(' | '));
    });
  });
  return lines.join('\n');
}

export async function extractOpsDocText(file) {
  if (!file) throw new Error('No file selected');
  const name = String(file.name || 'document');
  const type = String(file.type || '');
  const lower = name.toLowerCase();

  if (type.startsWith('text/') || /\.(txt|md|markdown|csv|json|log)$/i.test(lower)) {
    return {
      fileName: name,
      text: truncate(await file.text())
    };
  }

  if (type === 'application/pdf' || lower.endsWith('.pdf')) {
    return {
      fileName: name,
      text: truncate(await extractPdfText(file))
    };
  }

  if (
    lower.endsWith('.xlsx') ||
    lower.endsWith('.xls') ||
    type.includes('spreadsheet') ||
    type.includes('excel')
  ) {
    return {
      fileName: name,
      text: truncate(await extractExcelText(file))
    };
  }

  throw new Error('Supported docs: TXT, MD, CSV, JSON, PDF, XLSX');
}
